import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Save, Edit2, X, Copy, Check, Mail, Send, Calendar, User,
  Clock, Tag, Users, Image, Code, Eye, Settings, FileText
} from 'lucide-react';

interface EmailPreviewProps {
  asset: {
    id: number;
    file_name: string;
    asset_type: string;
    content?: string;
    metadata?: any;
    r2_key?: string;
  };
  apiBaseUrl: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function EmailPreview({ asset, apiBaseUrl, onSave }: EmailPreviewProps) {
  // Parse metadata
  const parseMetadata = () => {
    if (asset.metadata) {
      if (typeof asset.metadata === 'string') {
        try {
          return JSON.parse(asset.metadata);
        } catch (e) {
          return {};
        }
      }
      return asset.metadata;
    }
    return {};
  };

  const initialMetadata = parseMetadata();

  // State management
  const [activeTab, setActiveTab] = useState<'preview' | 'testing' | 'settings'>('preview');
  const [previewMode, setPreviewMode] = useState<'html' | 'image'>('html');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(asset.content || '');
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(0);

  // Email metadata
  const [emailMetadata, setEmailMetadata] = useState({
    subject: initialMetadata.subject || '',
    preheader: initialMetadata.preheader || '',
    from_name: initialMetadata.from_name || '',
    from_email: initialMetadata.from_email || '',
    reply_to: initialMetadata.reply_to || '',
    send_date: initialMetadata.send_date || '',
    send_time: initialMetadata.send_time || '',
    campaign_name: initialMetadata.campaign_name || '',
    segment: initialMetadata.segment || '',
    tags: Array.isArray(initialMetadata.tags) ? initialMetadata.tags.join(', ') : initialMetadata.tags || '',
    test_emails: initialMetadata.test_emails || '',
    utm_source: initialMetadata.utm_source || 'email',
    utm_medium: initialMetadata.utm_medium || 'campaign',
    utm_campaign: initialMetadata.utm_campaign || ''
  });

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'bullet',
    'link', 'image', 'color', 'background'
  ];

  const handleSave = async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      const updatedData = {
        content: editedContent,
        metadata: {
          ...initialMetadata,
          ...emailMetadata,
          tags: emailMetadata.tags.split(',').map(t => t.trim()).filter(t => t)
        }
      };
      await onSave(asset.id, JSON.stringify(updatedData));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(asset.content || '');
    setIsEditing(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get variations from metadata
  const subjectVariations = initialMetadata.subject_variations || [];
  const preheaderVariations = initialMetadata.preheader_variations || [];
  const images = initialMetadata.images || [];

  // A/B Testing Tab
  const ABTestingTab = () => {
    // Winner prediction based on subject line best practices
    const predictWinner = (subjects: string[]) => {
      return subjects.map((subject, index) => {
        let score = 0;
        const reasons = [];

        // Length check (40-50 chars is optimal)
        if (subject.length >= 40 && subject.length <= 50) {
          score += 30;
          reasons.push('Optimal length (40-50 chars)');
        } else if (subject.length < 40) {
          reasons.push('Could be longer for more context');
        } else {
          reasons.push('Could be shorter for better visibility');
        }

        // Contains numbers
        if (/\d/.test(subject)) {
          score += 20;
          reasons.push('Contains numbers (increases open rate)');
        }

        // Contains emoji or special character
        if (/[🎉🎊✨🎁💡🚀⭐]/.test(subject)) {
          score += 15;
          reasons.push('Eye-catching emoji');
        }

        // Not all caps
        if (subject !== subject.toUpperCase()) {
          score += 10;
          reasons.push('Not all caps (avoids spam filters)');
        }

        // Personalization hints
        if (/you|your/i.test(subject)) {
          score += 15;
          reasons.push('Personalized language');
        }

        // Urgency words
        if (/today|now|limited|exclusive|last chance/i.test(subject)) {
          score += 10;
          reasons.push('Creates urgency');
        }

        return { subject, score, reasons, index };
      }).sort((a, b) => b.score - a.score);
    };

    const rankedSubjects = predictWinner(subjectVariations);
    const winner = rankedSubjects[0];

    return (
      <div className="h-full overflow-auto bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Subject Line Variations */}
          {subjectVariations.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-blue-600" />
                  Subject Line Variations ({subjectVariations.length})
                </h3>
                <span className="text-sm text-gray-500">
                  A/B Testing Ready
                </span>
              </div>

              <div className="space-y-3">
                {rankedSubjects.map(({ subject, score, reasons, index }) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      index === 0
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">
                            Variation {index + 1}
                          </span>
                          {index === 0 && (
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                              Predicted Winner
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {subject.length} chars
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium mb-2">{subject}</p>
                        <div className="flex flex-wrap gap-1">
                          {reasons.map((reason, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded"
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="text-2xl font-bold text-gray-900">{score}</div>
                        <div className="text-xs text-gray-500">Score</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>💡 Tip:</strong> The platform will automatically rotate these variations and optimize based on open rates.
                </p>
              </div>
            </div>
          )}

          {/* Preheader Variations */}
          {preheaderVariations.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-purple-600" />
                  Preheader Variations ({preheaderVariations.length})
                </h3>
              </div>

              <div className="space-y-3">
                {preheaderVariations.map((preheader, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-500">
                        Variation {index + 1}
                      </span>
                      <span className="text-xs text-gray-400">
                        {preheader.length} / 150 chars
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm italic">{preheader}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Best Practice:</strong> Keep preheaders between 85-100 characters for optimal display.
                </p>
              </div>
            </div>
          )}

          {/* Image Gallery */}
          {images.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold flex items-center mb-4">
                <Image className="w-5 h-5 mr-2 text-indigo-600" />
                Email Images ({images.length})
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {images.map((img: any, index: number) => (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div className="aspect-video bg-gray-100 flex items-center justify-center">
                      <Image className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="p-2">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {img.filename || `Image ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(img.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {subjectVariations.length === 0 && preheaderVariations.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No A/B Testing Variations
              </h3>
              <p className="text-gray-600">
                This email doesn't have any subject line or preheader variations for testing.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Email Preview Tab
  const EmailPreviewTab = () => (
    <div className="h-full flex flex-col">
      {/* Preview Mode Toggle */}
      <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-600">Preview Mode:</span>
          <div className="flex space-x-1">
            <button
              onClick={() => setPreviewMode('html')}
              className={`px-3 py-1 rounded text-sm ${
                previewMode === 'html'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Code className="w-4 h-4 inline mr-1" />
              HTML
            </button>
            <button
              onClick={() => setPreviewMode('image')}
              className={`px-3 py-1 rounded text-sm ${
                previewMode === 'image'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Image className="w-4 h-4 inline mr-1" />
              Image
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            <span className="text-sm">{copied ? 'Copied!' : 'Copy HTML'}</span>
          </button>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm">Edit Content</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                <Save className="w-4 h-4" />
                <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Email Preview Content */}
      <div className="flex-1 overflow-auto bg-gray-100 p-4">
        <div className="max-w-3xl mx-auto">
          {previewMode === 'html' ? (
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              {/* Email Header Mockup */}
              <div className="bg-gray-50 px-6 py-4 border-b">
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-20">From:</span>
                    <span className="text-gray-900">
                      {emailMetadata.from_name || 'Sender'} &lt;{emailMetadata.from_email || 'email@example.com'}&gt;
                    </span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-20">To:</span>
                    <span className="text-gray-900">subscriber@example.com</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-20">Subject:</span>
                    <span className="text-gray-900 font-medium">
                      {emailMetadata.subject || 'Email Subject Line'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="bg-white">
                {emailMetadata.preheader && (
                  <div className="px-6 py-2 bg-gray-50 text-xs text-gray-600 italic border-b">
                    {emailMetadata.preheader}
                  </div>
                )}

                <div className="px-6 py-6">
                  {isEditing ? (
                    <ReactQuill
                      value={editedContent}
                      onChange={setEditedContent}
                      modules={quillModules}
                      formats={quillFormats}
                      className="min-h-[400px]"
                      placeholder="Compose your email content here..."
                    />
                  ) : (
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: editedContent || '<p class="text-gray-500">No content yet. Click Edit Content to add email body.</p>'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Email Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t text-xs text-center text-gray-600">
                <p>© 2024 Your Company. All rights reserved.</p>
                <p className="mt-2">
                  123 Main Street, City, State 12345<br />
                  <a href="#" className="text-blue-600 hover:underline">Unsubscribe</a>
                  {' • '}
                  <a href="#" className="text-blue-600 hover:underline">Preferences</a>
                  {' • '}
                  <a href="#" className="text-blue-600 hover:underline">View in browser</a>
                </p>
              </div>
            </div>
          ) : (
            // Image preview mode
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              {asset.r2_key ? (
                <img
                  src={`${apiBaseUrl}/assets/preview/${asset.r2_key}`}
                  alt="Email preview"
                  className="w-full"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`p-12 text-center text-gray-500 ${asset.r2_key ? 'hidden' : ''}`}>
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No image preview available</p>
                <p className="text-sm mt-2">Upload an image file or switch to HTML view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Email Settings Tab
  const EmailSettingsTab = () => (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold flex items-center">
              <Settings className="w-5 h-5 mr-2 text-blue-600" />
              Email Campaign Settings
            </h3>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Basic Settings */}
            <div className="col-span-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b">Basic Information</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name
              </label>
              <input
                type="text"
                value={emailMetadata.campaign_name}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, campaign_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Q4 Newsletter Campaign"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject Line *
              </label>
              <input
                type="text"
                value={emailMetadata.subject}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, subject: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your email subject..."
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                {emailMetadata.subject.length}/100 characters
              </p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preheader Text
              </label>
              <textarea
                value={emailMetadata.preheader}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, preheader: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                placeholder="Preview text shown in inbox..."
                maxLength={150}
              />
              <p className="text-xs text-gray-500 mt-1">
                {emailMetadata.preheader.length}/150 characters
              </p>
            </div>

            {/* Sender Settings */}
            <div className="col-span-2 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b">Sender Information</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Name *
              </label>
              <input
                type="text"
                value={emailMetadata.from_name}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, from_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Company"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Email *
              </label>
              <input
                type="email"
                value={emailMetadata.from_email}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, from_email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="noreply@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reply-To Email
              </label>
              <input
                type="email"
                value={emailMetadata.reply_to}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, reply_to: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="support@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Email Addresses
              </label>
              <input
                type="text"
                value={emailMetadata.test_emails}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, test_emails: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="test1@example.com, test2@example.com"
              />
            </div>

            {/* Scheduling */}
            <div className="col-span-2 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b">Scheduling & Delivery</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Send Date
              </label>
              <input
                type="date"
                value={emailMetadata.send_date}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, send_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4 inline mr-1" />
                Send Time
              </label>
              <input
                type="time"
                value={emailMetadata.send_time}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, send_time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Segmentation */}
            <div className="col-span-2 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b">Targeting & Segmentation</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Segment
              </label>
              <input
                type="text"
                value={emailMetadata.segment}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, segment: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="All Subscribers"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tag className="w-4 h-4 inline mr-1" />
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={emailMetadata.tags}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, tags: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="newsletter, promotional, q4"
              />
            </div>

            {/* UTM Parameters */}
            <div className="col-span-2 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b">UTM Tracking Parameters</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UTM Source
              </label>
              <input
                type="text"
                value={emailMetadata.utm_source}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, utm_source: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UTM Medium
              </label>
              <input
                type="text"
                value={emailMetadata.utm_medium}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, utm_medium: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="campaign"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UTM Campaign
              </label>
              <input
                type="text"
                value={emailMetadata.utm_campaign}
                onChange={(e) => setEmailMetadata({ ...emailMetadata, utm_campaign: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="q4-newsletter-2024"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header with Tabs */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Email Campaign Editor</h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 rounded ${
                activeTab === 'preview'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-1" />
              Email Preview
            </button>
            <button
              onClick={() => setActiveTab('testing')}
              className={`px-4 py-2 rounded ${
                activeTab === 'testing'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-1" />
              A/B Testing
              {(subjectVariations.length + preheaderVariations.length) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">
                  {subjectVariations.length + preheaderVariations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 rounded ${
                activeTab === 'settings'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-1" />
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'preview' && <EmailPreviewTab />}
        {activeTab === 'testing' && <ABTestingTab />}
        {activeTab === 'settings' && <EmailSettingsTab />}
      </div>
    </div>
  );
}